import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { advancedBillingService } from '../../services/advancedBillingService';
import { getTranslation } from '../../utils/translations';
import MarketConfigurationSection from './components/MarketConfigurationSection';
import LanguageManagementSection from './components/LanguageManagementSection';
import ComplianceTrackingSection from './components/ComplianceTrackingSection';
import MarketAnalyticsSection from './components/MarketAnalyticsSection';
import AddCountryModal from './components/AddCountryModal';
import RegionManagementModal from './components/RegionManagementModal';

const InternationalMarketManagement = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'FR';
  });
  const [activeTab, setActiveTab] = useState('markets');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddCountryModalOpen, setIsAddCountryModalOpen] = useState(false);
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);

  const t = getTranslation(currentLanguage);

  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await advancedBillingService?.getAllCountries();
      setCountries(data);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setError(err?.message || 'Error loading countries');
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: t?.international?.title || 'International Management', path: '/international-market-management', icon: 'Globe' }
  ];

  const tabs = [
    { id: 'markets', label: t?.international?.markets || 'Markets', icon: 'Globe' },
    { id: 'languages', label: t?.international?.languages || 'Languages', icon: 'Languages' },
    { id: 'compliance', label: t?.international?.compliance || 'Compliance', icon: 'Shield' },
    { id: 'analytics', label: t?.billing?.analytics || 'Analytics', icon: 'BarChart3' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'markets':
        return <MarketConfigurationSection countries={countries} onRefresh={fetchCountries} language={currentLanguage} />;
      case 'languages':
        return <LanguageManagementSection language={currentLanguage} />;
      case 'compliance':
        return <ComplianceTrackingSection countries={countries} language={currentLanguage} />;
      case 'analytics':
        return <MarketAnalyticsSection countries={countries} language={currentLanguage} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="max-w-7xl mx-auto space-y-6">
            <Breadcrumb items={breadcrumbItems} />
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t?.international?.title || 'International Market Management'}
                </h1>
                <p className="text-muted-foreground">
                  {t?.international?.subtitle || 'Manage global markets, compliance, and localization'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRegionModal(true)}
                >
                  <Icon name="Globe" size={18} className="mr-2" />
                  {currentLanguage === 'FR' ? 'Gérer les Régions' : currentLanguage === 'EN' ? 'Manage Regions' : 'Zarządzaj Regionami'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowAddCountryModal(true)}
                >
                  <Icon name="Plus" size={18} className="mr-2" />
                  {t?.international?.addCountry || 'Add Country'}
                </Button>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border">
              <div className="border-b border-border">
                <div className="flex space-x-1 p-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span className="font-medium">{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-3 text-muted-foreground">{t?.common?.loading || 'Loading...'}</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <Icon name="AlertCircle" size={48} className="mx-auto text-destructive mb-4" />
                    <p className="text-muted-foreground">{error}</p>
                    <Button variant="outline" size="sm" onClick={fetchCountries} className="mt-4">
                      {currentLanguage === 'FR' && 'Réessayer'}
                      {currentLanguage === 'EN' && 'Retry'}
                      {currentLanguage === 'PL' && 'Spróbuj ponownie'}
                    </Button>
                  </div>
                ) : (
                  renderTabContent()
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      <AddCountryModal
        isOpen={showAddCountryModal}
        onClose={() => setShowAddCountryModal(false)}
        onSuccess={fetchCountries}
        language={currentLanguage}
      />

      <RegionManagementModal
        isOpen={showRegionModal}
        onClose={() => setShowRegionModal(false)}
        language={currentLanguage}
      />
    </div>
  );
};

export default InternationalMarketManagement;